// Enum管理ユーティリティクラス
// 動的にenum型を管理し、新しいenum型の追加を容易にする

// enum型は自動生成されたenumRegistry.tsでインポート・登録されます
import { enumDocumentationData } from "../api/generated/EnumDocs";

export interface EnumInfo {
  name: string;
  enumObject: Record<string, number>;
  labelFormatter?: (value: number, name: string) => string;
}

export class EnumManager {
  private static readonly ENUM_REGISTRY: Record<string, EnumInfo> = {};
  private static _initialized = false;

  /**
   * Enum管理システムを初期化（自動生成されたenum登録により不要になったが、後方互換性のため残す）
   */
  private static initialize(): void {
    // 自動生成されたenum登録により、この関数は不要になりました
    // 後方互換性のため空の実装を残します
    this._initialized = true;
  }

  /**
   * 新しいenum型を登録
   */
  static registerEnum(
    enumName: string,
    enumObject: Record<string, number>,
    labelFormatter?: (value: number, name: string) => string
  ): void {
    this.ENUM_REGISTRY[enumName] = {
      name: enumName,
      enumObject: enumObject as Record<string, number>,
      labelFormatter,
    };
  }

  /**
   * enum型名からenum定数オブジェクトを取得
   */
  static getEnumObject(enumType: string): Record<string, number> | null {
    this.initialize();

    // "consts.MaintenanceType" -> "MaintenanceType"
    const enumName = enumType.replace("consts.", "");

    const enumInfo = this.ENUM_REGISTRY[enumName];
    if (enumInfo) {
      return enumInfo.enumObject;
    } else {
      console.warn(
        `Unknown enum type: ${enumType}. Available types:`,
        this.getAvailableEnums()
      );
      return null;
    }
  }

  /**
   * enum型名から表示用のラベルフォーマッターを取得
   * enumDocumentationDataからcommentを取得して日本語名を表示
   */
  static getEnumLabelFormatter(
    enumType: string
  ): (value: number, name: string) => string {
    this.initialize();

    const enumName = enumType.replace("consts.", "");
    const enumInfo = this.ENUM_REGISTRY[enumName];

    // enumDocumentationDataから該当するenum情報を取得
    const enumDocInfo = enumDocumentationData.find(
      (doc) => doc.typeName === enumName
    );

    // enumDocumentationDataからcommentを取得するフォーマッターを作成
    if (enumDocInfo) {
      return (value: number, name: string) => {
        // enumDocumentationDataから該当する値のcommentを取得
        // constant_pb.tsの短縮名（例: FREE_DIA）とEnumDocs.tsのフルネーム（例: ITEM_TYPE_FREE_DIA）を
        // 末尾一致で比較する。名前が完全に一致しない場合はvalueのみで比較する。
        let valueInfo = enumDocInfo.values.find(
          (v) => Number(v.value) === value && v.name.endsWith(name)
        );

        // 末尾一致で見つからない場合はvalueのみで比較
        if (!valueInfo) {
          valueInfo = enumDocInfo.values.find((v) => Number(v.value) === value);
        }

        // カスタムフォーマッターがあればそれを使用
        if (enumInfo?.labelFormatter) {
          return enumInfo.labelFormatter(value, name);
        }

        // commentがあればそれを使用
        if (valueInfo?.comment) {
          const comment = valueInfo.comment;
          // commentが「英語名 日本語名」の形式の場合、日本語部分を抽出
          // スペース以降の日本語部分を取得（日本語文字が含まれる部分）
          const spaceIndex = comment.indexOf(" ");
          let displayName = comment;

          if (spaceIndex > 0) {
            // スペース以降の部分を取得
            const afterSpace = comment.substring(spaceIndex + 1);
            // 日本語文字（ひらがな、カタカナ、漢字）が含まれているかチェック
            const hasJapanese =
              /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(afterSpace);
            if (hasJapanese && afterSpace.trim()) {
              displayName = afterSpace.trim();
            } else {
              // 日本語が含まれていない場合は、英語名部分を除去して日本語部分を探す
              // または、comment全体を使用
              displayName = comment;
            }
          }

          // commentが空でない場合はそれを使用
          if (displayName && displayName.trim()) {
            return `${value}: ${displayName}`;
          }
        }

        // commentがない場合はnameを使用
        return `${value}: ${name}`;
      };
    }

    // 対応するフォーマッターがあれば使用、なければデフォルト
    return (
      enumInfo?.labelFormatter ||
      ((value: number, name: string) => `${value}: ${name}`)
    );
  }

  /**
   * 利用可能なenum型の一覧を取得
   */
  static getAvailableEnums(): string[] {
    this.initialize();
    return Object.keys(this.ENUM_REGISTRY);
  }

  /**
   * 登録されているenum情報を取得
   */
  static getEnumInfo(enumName: string): EnumInfo | null {
    this.initialize();
    return this.ENUM_REGISTRY[enumName] || null;
  }

  /**
   * 全てのenum情報を取得
   */
  static getAllEnumInfo(): EnumInfo[] {
    this.initialize();
    return Object.values(this.ENUM_REGISTRY);
  }

  /**
   * enum型が登録されているかチェック
   */
  static isEnumRegistered(enumType: string): boolean {
    this.initialize();
    const enumName = enumType.replace("consts.", "");
    return enumName in this.ENUM_REGISTRY;
  }

  /**
   * デフォルトのラベルフォーマッターを生成
   * プレフィックスパターンに基づいて自動的にフォーマッターを作成
   */
  static createDefaultLabelFormatter(
    prefix: string
  ): (value: number, name: string) => string {
    return (value: number, name: string) => {
      const displayName = name
        .replace(new RegExp(`^${prefix}_`), "")
        .toLowerCase();
      return `${value}: ${
        displayName.charAt(0).toUpperCase() + displayName.slice(1)
      }`;
    };
  }
}

// デフォルトエクスポート
export default EnumManager;
